import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Play, Pause, Trash2, Plus, X, Users, Clock, CheckCircle2, AlertTriangle, Loader2,
} from "lucide-react";

const AUDIENCE_OPTIONS = [
  { value: "parents_active",          label: "Facility Members · with active ward enrolments", desc: "Recommended default. Only members whose kids are currently in a batch." },
  { value: "parents_all",             label: "Facility Members · all",                          desc: "Every registered facility member with a phone number — biggest reach." },
  { value: "parents_kyc_incomplete",  label: "Facility Members · KYC incomplete",               desc: "Members missing their own KYC OR having a ward with missing docs." },
  { value: "members_active",          label: "Membership Subscribers · currently valid",         desc: "Only Membership Subscribers holding an active gym / swim / court / turf plan right now." },
  { value: "members_all",             label: "Membership Subscribers · all accounts",             desc: "Every Membership Subscriber account, whether they hold a live plan or not. Matches the Membership Subscribers table exactly." },
  { value: "booking_guests_90d",      label: "Slot booking guests · last 90 days",     desc: "Anyone who booked a court/turf slot in the last 3 months — hot leads." },
  { value: "booking_guests_all",      label: "Slot booking guests · all time",         desc: "Every guest / member who's ever booked a slot. Biggest customer reach." },
  { value: "all_contacts",            label: "Everyone (Facility Members + Subscribers + 90d guests + verified sign-ups)", desc: "Union of every branch below. Broadest reach, but use sparingly to avoid ban risk." },
  { value: "hub_members_all",         label: "All verified sign-ups (any account, bought or not)", desc: "Every member who signed up — Google or Email — and verified their WhatsApp, even if they never booked / bought anything. This is the master list of registered contacts." },
  { value: "custom",                  label: "Custom list (paste phone numbers)",      desc: "One-off blast to a manual list. Numbers separated by comma / newline." },
];

const RATE_PRESETS = [
  { value: 10, label: "Safe · 10/hr",       hint: "Best for the first month" },
  { value: 20, label: "Balanced · 20/hr",   hint: "Recommended default" },
  { value: 50, label: "Faster · 50/hr",     hint: "Only after months of good standing" },
  { value: 100, label: "Aggressive · 100/hr", hint: "Ban risk higher — use with caution" },
];

export default function AdminWhatsappCampaigns() {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/admin/whatsapp/bridge/campaigns"); setList(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const startCamp = async (c) => {
    try { await api.post(`/admin/whatsapp/bridge/campaigns/${c.id}/start`); toast.success(`${c.name} started`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pauseCamp = async (c) => {
    try { await api.post(`/admin/whatsapp/bridge/campaigns/${c.id}/pause`); toast.success(`${c.name} paused`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteCamp = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and its queue? This cannot be undone.`)) return;
    try { await api.delete(`/admin/whatsapp/bridge/campaigns/${c.id}`); toast.success("Campaign deleted"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">// Broadcast · Bulk WhatsApp</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter">WhatsApp Campaigns</h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Schedule bulk messages through your paired in-house bridge. Each campaign is drip-fed with jitter and quiet hours
            so your number stays safe.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-campaign-btn"
          className="bg-brand-orange text-black px-5 py-3 font-bold text-xs uppercase tracking-widest hover:opacity-90 flex items-center gap-2">
          <Plus size={14} /> New Campaign
        </button>
      </div>

      {loading && <div className="text-white/40 py-6">Loading…</div>}
      {!loading && list.length === 0 && (
        <div className="border border-white/10 p-8 text-center text-white/50" data-testid="campaigns-empty">
          <MessageSquare className="mx-auto mb-3 text-white/30" size={36} />
          No campaigns yet. Hit <b>New Campaign</b> to schedule your first drip.
        </div>
      )}

      <div className="space-y-3">
        {list.map((c) => (
          <CampaignRow key={c.id} c={c}
            onStart={() => startCamp(c)}
            onPause={() => pauseCamp(c)}
            onDelete={() => deleteCamp(c)}
            onOpen={() => setDetailFor(c.id)} />
        ))}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {detailFor && <DetailModal id={detailFor} onClose={() => setDetailFor(null)} />}
    </div>
  );
}

function CampaignRow({ c, onStart, onPause, onDelete, onOpen }) {
  const pct = c.audience_count ? Math.round((c.sent / c.audience_count) * 100) : 0;
  const running = c.status === "running";
  const readRate = c.sent ? Math.round((c.read / c.sent) * 100) : 0;
  const delivRate = c.sent ? Math.round((c.delivered / c.sent) * 100) : 0;
  return (
    <div className="border border-white/12 bg-[#131A2B] p-5" data-testid={`campaign-row-${c.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black uppercase text-lg">{c.name}</span>
            <StatusPill status={c.status} />
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
              {c.rate_per_hour}/hr
            </span>
          </div>
          <div className="text-xs text-white/50 mt-1 flex flex-wrap gap-3">
            <span><Users size={11} className="inline mr-1" />{c.audience_count} recipients</span>
            <span>Sent {c.sent}</span>
            <span className="text-white/70">Delivered {c.delivered || 0}{c.sent ? ` (${delivRate}%)` : ""}</span>
            <span className="text-brand-blue">Read {c.read || 0}{c.sent ? ` (${readRate}%)` : ""}</span>
            {c.failed > 0 && <span className="text-red-400">Failed {c.failed}</span>}
            {c.started_at && <span><Clock size={11} className="inline mr-1" />started {new Date(c.started_at).toLocaleString("en-IN")}</span>}
          </div>
          <div className="mt-3 h-1.5 bg-black/40 border border-white/8 relative overflow-hidden">
            {/* layered bars: sent (orange), delivered (light blue), read (brand blue) */}
            <div className="absolute inset-y-0 left-0 bg-brand-orange/60" style={{ width: `${pct}%` }} />
            <div className="absolute inset-y-0 left-0 bg-brand-blue/60" style={{ width: `${c.audience_count ? Math.round((c.delivered / c.audience_count) * 100) : 0}%` }} />
            <div className="absolute inset-y-0 left-0 bg-brand-blue" style={{ width: `${c.audience_count ? Math.round((c.read / c.audience_count) * 100) : 0}%` }} />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onOpen} data-testid={`campaign-open-${c.id}`}
            className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-2 hover:bg-white/10">
            Open
          </button>
          {c.status !== "completed" && (
            running ? (
              <button onClick={onPause} data-testid={`campaign-pause-${c.id}`}
                className="text-xs font-mono uppercase tracking-widest border border-yellow-500 text-yellow-300 px-3 py-2 hover:bg-yellow-500/10 flex items-center gap-1">
                <Pause size={12} /> Pause
              </button>
            ) : (
              <button onClick={onStart} data-testid={`campaign-start-${c.id}`}
                className="text-xs font-mono uppercase tracking-widest bg-green-600 text-white px-3 py-2 hover:bg-green-500 flex items-center gap-1">
                <Play size={12} /> Start
              </button>
            )
          )}
          <button onClick={onDelete} data-testid={`campaign-delete-${c.id}`}
            className="text-xs font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-3 py-2 hover:bg-red-500 hover:text-white flex items-center gap-1">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:     ["bg-white/15 text-white/70", "Draft"],
    running:   ["bg-green-500 text-white", "Running"],
    paused:    ["bg-yellow-500 text-black", "Paused"],
    completed: ["bg-brand-blue text-white", "Completed"],
  };
  const [cls, label] = map[status] || map.draft;
  return <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${cls}`}>{label}</span>;
}

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [audience, setAudience] = useState("parents_active");
  const [rate, setRate] = useState(20);
  const [message, setMessage] = useState("Hi {name}, this is HTP Sports Hub — ");
  const [customNumbers, setCustomNumbers] = useState("");
  const [respect, setRespect] = useState(true);
  const [personalise, setPersonalise] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const nums = customNumbers.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/whatsapp/bridge/campaigns/preview", {
        audience, custom_numbers: audience === "custom" ? nums : [],
      });
      setPreview(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setPreviewing(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const nums = customNumbers.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/whatsapp/bridge/campaigns", {
        name, audience, rate_per_hour: rate, message,
        respect_quiet_hours: respect, personalise,
        custom_numbers: audience === "custom" ? nums : [],
      });
      toast.success("Campaign queued — click Start when ready");
      onCreated();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={create} onClick={(e) => e.stopPropagation()} className="bg-[#0B1120] border border-white/12 max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6" data-testid="campaign-create-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">// New WhatsApp campaign</div>
            <h2 className="font-display font-black uppercase text-2xl">Create Campaign</h2>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Campaign name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="camp-name" placeholder="March renewal reminder"
          className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm mb-4 focus:outline-none focus:border-brand-orange" />

        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Audience</label>
        <div className="space-y-2 mb-4">
          {AUDIENCE_OPTIONS.map((o) => (
            <label key={o.value} className={`flex items-start gap-3 border p-3 cursor-pointer ${audience === o.value ? "border-brand-orange bg-brand-orange/8" : "border-white/12"}`} data-testid={`camp-aud-${o.value}`}>
              <input type="radio" name="aud" checked={audience === o.value} onChange={() => setAudience(o.value)} className="mt-1 accent-brand-orange" />
              <div>
                <div className="font-bold text-sm">{o.label}</div>
                <div className="text-[11px] text-white/50">{o.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {audience === "custom" && (
          <textarea value={customNumbers} onChange={(e) => setCustomNumbers(e.target.value)}
            rows={4} placeholder="+919812345678, +919876543210 or one per line"
            data-testid="camp-custom-numbers"
            className="w-full bg-black/40 border border-white/15 px-3 py-2 text-xs font-mono mb-4 focus:outline-none focus:border-brand-orange" />
        )}

        {/* Preview audience */}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={runPreview} disabled={previewing}
            data-testid="camp-preview-btn"
            className="text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white disabled:opacity-50 flex items-center gap-1">
            {previewing ? "Loading…" : "🔍 Preview audience"}
          </button>
          {preview && (
            <div className="text-xs text-white/70" data-testid="camp-preview-count">
              <b className="text-2xl font-display brand-blue">{preview.count}</b>{" "}
              recipients {preview.sources && (
                <span className="text-white/50 ml-1">
                  ({Object.entries(preview.sources).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")})
                </span>
              )}
            </div>
          )}
        </div>

        {preview && preview.count > 0 && (
          <div className="mb-4 border border-white/12 bg-black/40 p-3 max-h-40 overflow-y-auto" data-testid="camp-preview-list">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// First {Math.min(preview.count, 10)} phones (sample)</div>
            <ul className="space-y-1 text-xs font-mono">
              {(preview.sample || []).slice(0, 10).map((r, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="text-white/80 truncate">{r.name || "(no name)"}</span>
                  <span className="text-white/60">{r.phone}</span>
                  <span className="text-white/40 text-[10px] uppercase">{r.source}</span>
                </li>
              ))}
            </ul>
            {preview.truncated && <div className="text-[10px] text-white/40 mt-2">…and {preview.count - 10} more (list truncated)</div>}
          </div>
        )}

        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Message (use <b>{"{name}"}</b> for personalisation)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={4}
          data-testid="camp-message"
          className="w-full bg-black/40 border border-white/15 px-3 py-2.5 text-sm mb-4 focus:outline-none focus:border-brand-orange" />

        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Sending speed</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {RATE_PRESETS.map((r) => (
            <button type="button" key={r.value} onClick={() => setRate(r.value)}
              data-testid={`camp-rate-${r.value}`}
              className={`text-left border px-3 py-2 ${rate === r.value ? "border-brand-orange bg-brand-orange/10" : "border-white/15 hover:border-white/40"}`}>
              <div className={`font-bold text-sm ${rate === r.value ? "brand-orange" : ""}`}>{r.label}</div>
              <div className="text-[10px] text-white/50 mt-0.5">{r.hint}</div>
            </button>
          ))}
        </div>

        <div className="space-y-2 mb-6 text-xs text-white/70">
          <label className="flex gap-2 items-center cursor-pointer">
            <input type="checkbox" checked={respect} onChange={(e) => setRespect(e.target.checked)} data-testid="camp-respect-quiet" className="accent-brand-orange" />
            Skip quiet hours (22:00 – 08:00 IST) — highly recommended
          </label>
          <label className="flex gap-2 items-center cursor-pointer">
            <input type="checkbox" checked={personalise} onChange={(e) => setPersonalise(e.target.checked)} data-testid="camp-personalise" className="accent-brand-orange" />
            Personalise with recipient&apos;s first name
          </label>
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={busy} data-testid="camp-submit"
            className="flex-1 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
            {busy ? "Creating…" : "Queue Campaign"}
          </button>
          <button type="button" onClick={onClose} className="px-4 border border-white/20 text-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function DetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data: d } = await api.get(`/admin/whatsapp/bridge/campaigns/${id}`); setData(d); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);

  if (!data) return null;
  const counts = data.counts || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0B1120] border border-white/12 max-w-4xl w-full max-h-[92vh] overflow-y-auto p-6" data-testid="campaign-detail-modal">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">// Campaign detail</div>
            <h2 className="font-display font-black uppercase text-2xl">{data.name}</h2>
            <div className="text-xs text-white/50 mt-1">{data.audience_type} · {data.audience_count} recipients · {data.rate_per_hour}/hr</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        {(counts.failed || 0) > 0 && (
          <div className="border-2 border-red-500/40 bg-red-500/10 p-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="detail-retry-bar">
            <div className="text-xs">
              <b className="text-red-300 font-mono uppercase tracking-widest">{counts.failed} failed row(s).</b>
              <span className="text-white/60 ml-2">Fix the WhatsApp config, then click retry to resend just the failed ones.</span>
            </div>
            <button
              type="button"
              data-testid="btn-retry-failed"
              onClick={async () => {
                try {
                  const r = await api.post(`/admin/whatsapp/bridge/campaigns/${id}/retry-failed`);
                  toast.success(`Requeued ${r.data.requeued} row(s)`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Retry failed");
                }
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest font-bold"
            >
              ↻ Retry failed
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
          {["queued", "sending", "sent", "delivered", "read", "failed"].map((k) => {
            const val = counts[k] || 0;
            const cls = k === "read" ? "text-brand-blue" : k === "delivered" ? "text-cyan-300" : k === "sent" ? "text-white/80" : k === "failed" ? "text-red-400" : "text-white/70";
            return (
              <div key={k} className="border border-white/12 p-3 text-center" data-testid={`detail-count-${k}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{k}</div>
                <div className={`font-display text-2xl ${cls}`}>{val}</div>
              </div>
            );
          })}
        </div>

        {/* Blue-tick funnel */}
        <div className="border border-brand-blue/40 bg-brand-blue/5 p-3 mb-4 flex flex-wrap gap-6 text-xs" data-testid="detail-funnel">
          <div>
            <span className="text-white/50 uppercase tracking-widest text-[10px]">Delivery rate · </span>
            <b className="text-cyan-300">
              {data.audience_count ? Math.round(((data.delivered || 0) / data.audience_count) * 100) : 0}%
            </b>
            <span className="text-white/40 ml-1">({data.delivered || 0}/{data.audience_count})</span>
          </div>
          <div>
            <span className="text-white/50 uppercase tracking-widest text-[10px]">Read rate (blue ticks) · </span>
            <b className="brand-blue">
              {data.audience_count ? Math.round(((data.read || 0) / data.audience_count) * 100) : 0}%
            </b>
            <span className="text-white/40 ml-1">({data.read || 0}/{data.audience_count})</span>
          </div>
        </div>

        <div className="border border-white/12 bg-black/30 p-3 mb-4 text-xs font-mono text-white/70 whitespace-pre-wrap">{data.message}</div>

        <div className="border border-white/12 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-white/5">
              <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
                <th className="p-2">Recipient</th>
                <th className="p-2">Phone</th>
                <th className="p-2">Scheduled</th>
                <th className="p-2">Sent</th>
                <th className="p-2">Delivered</th>
                <th className="p-2">Read</th>
                <th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.outbox || []).map((o) => (
                <tr key={o.id} className="border-t border-white/8" data-testid={`outbox-row-${o.id}`}>
                  <td className="p-2">{o.name || "-"}</td>
                  <td className="p-2 font-mono">{o.to}</td>
                  <td className="p-2 text-white/50">{o.scheduled_at ? new Date(o.scheduled_at).toLocaleString("en-IN") : "-"}</td>
                  <td className="p-2 text-white/50">{o.sent_at ? new Date(o.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                  <td className="p-2 text-cyan-300">{o.delivered_at ? new Date(o.delivered_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="p-2 brand-blue font-bold">{o.read_at ? new Date(o.read_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className={`p-2 uppercase text-[10px] font-mono tracking-widest ${
                    o.status === "read" ? "text-brand-blue" :
                    o.status === "delivered" ? "text-cyan-300" :
                    o.status === "sent" ? "text-green-400" :
                    o.status === "failed" ? "text-red-400" :
                    o.status === "sending" ? "text-brand-orange" :
                    "text-white/50"
                  }`}>{o.status}{o.error ? ` · ${o.error.slice(0, 40)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
